import React, { useState } from 'react';
import { MoreVertical, Globe, Presentation, Loader2, UserPlus, Settings, Download, Sparkles, ListChecks } from 'lucide-react';
import type { ArtifactKind } from '../../services/artifactService';
import { TYPE } from '../../constants/typeScale';

/**
 * Chat overflow menu (top-right ⋯): invite people, manage the group, generate NOTES from the
 * discussion (a consultant-style brief/deck the agents distil from the chat), or download the
 * raw transcript. "Notes Generation" scans the conversation and produces a shareable artifact —
 * vs. "Download transcript" which is just the raw messages as text.
 */
const EXPORTS: Array<{ kind: ArtifactKind; format?: 'html'; label: string; Icon: React.FC<{ className?: string }> }> = [
  { kind: 'summary', format: 'html', label: 'Decision brief (web page)', Icon: Globe },
  { kind: 'deck', label: 'Slide deck (PowerPoint)', Icon: Presentation },
];

interface Props {
  onExport: (kind: ArtifactKind, format?: 'html') => void;
  onExportTranscript?: (format: 'txt') => void;
  onInvite?: () => void;
  onManage?: () => void;
  onRequests?: () => void;
  onDecisions?: () => void;
  busy?: boolean;
}

const ChatHeaderMenu: React.FC<Props> = ({ onExport, onExportTranscript, onInvite, onManage, onRequests, onDecisions, busy }) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Chat options" title="Chat options"
        className="p-1.5 rounded-lg text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={close} />
          <div className="absolute right-0 top-full mt-1 w-56 rounded-xl border border-secondary-200 dark:border-secondary-600 bg-white dark:bg-secondary-800 shadow-xl overflow-hidden z-50">
            {(onInvite || onManage) && (
              <div className="border-b border-secondary-100 dark:border-secondary-600 py-1">
                {onInvite && (
                  <button onClick={() => { close(); onInvite(); }} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left ${TYPE.control} text-secondary-700 dark:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-700`}>
                    <UserPlus className="w-4 h-4 text-secondary-500" /> Invite people
                  </button>
                )}
                {onManage && (
                  <button onClick={() => { close(); onManage(); }} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left ${TYPE.control} text-secondary-700 dark:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-700`}>
                    <Settings className="w-4 h-4 text-secondary-500" /> Manage Group
                  </button>
                )}
                {onRequests && (
                  <button onClick={() => { close(); onRequests(); }} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left ${TYPE.control} text-secondary-700 dark:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-700`}>
                    <ListChecks className="w-4 h-4 text-secondary-500" /> Requests for your crew
                  </button>
                )}
              </div>
            )}
            {onDecisions && (
              <div className="border-b border-secondary-100 dark:border-secondary-600 py-1">
                <button onClick={() => { close(); onDecisions(); }} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left ${TYPE.control} text-secondary-700 dark:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-700`}>
                  <Sparkles className="w-4 h-4 text-secondary-500" /> Synthesize (decision brief)
                </button>
              </div>
            )}
            <p className={`px-3 pt-2 ${TYPE.label} text-secondary-400`}>Notes Generation</p>
            <p className={`px-3 pb-1 ${TYPE.meta} text-secondary-400`}>The team distils the discussion into shareable notes.</p>
            {EXPORTS.map(({ kind, format, label, Icon }) => (
              <button
                key={`${kind}-${format || ''}`}
                onClick={() => { close(); onExport(kind, format); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left ${TYPE.control} text-secondary-700 dark:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-700`}
              >
                <Icon className="w-4 h-4 text-secondary-500" />
                {label}
              </button>
            ))}
            {onExportTranscript && (
              <div className="border-t border-secondary-100 dark:border-secondary-600 mt-1 pt-1">
                <p className={`px-3 pb-1 ${TYPE.label} text-secondary-400`}>Download transcript</p>
                <button onClick={() => { close(); onExportTranscript('txt'); }} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left ${TYPE.control} text-secondary-700 dark:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-700`}>
                  <Download className="w-4 h-4 text-secondary-500" /> Text (.txt)
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ChatHeaderMenu;
